/**
 * What the account pays with: label, call to action, and the fine print.
 *
 * Two rules matter, and both mirror what the server does in confirmAndProduce so
 * the sheet never promises a charge that will not happen:
 *   - per_card: CREDIT IS SPENT BEFORE THE CARD. If the balance covers the fare,
 *     the sheet says "balance", not "card on file", or the customer reads the
 *     wrong thing and watches their balance drop instead.
 *   - buffer: this brief's fare is NOT charged now — it is added to a running
 *     total, and the card is charged €500 at a time. The sheet must not say
 *     "pay €X now" for a card that only accrues.
 *
 * Kept as a pure function so the rules can be asserted directly, without a
 * browser. It is the one piece of this flow that decides what a customer is
 * told they are about to be charged.
 *
 * @module web/paySource
 */
import { fmtEurCompact } from "./money.ts";

export type PayKind = "balance" | "card" | "buffer" | "sponsored" | "no-card";

export interface PaySource {
  kind: PayKind;
  label: string;
  cta: string;
  note: string;
  /** True when the sheet must refuse the tap. */
  blocked?: boolean;
}

export interface PayBrand {
  billingMode?: string | null;
  walletBalanceCents?: number | null;
  hasCardOnFile?: boolean | null;
  billingBlocked?: boolean | null;
  bufferLimitCents?: number | null;
}

export function paySource(brand: PayBrand | null, fareCents: number): PaySource {
  const b = brand ?? {};
  const balance = b.walletBalanceCents ?? 0;

  // Sponsored: Aditor covers it, the server charges nothing - so nothing to pay
  // is named and a missing card never blocks the tap.
  if (b.billingMode === "sponsored") {
    return {
      kind: "sponsored",
      label: "Sponsored by Aditor",
      cta: "Confirm",
      note: "No charge - Aditor covers this briefing.",
    };
  }

  if (b.billingMode === "buffer") {
    const step = fmtEurCompact(b.bufferLimitCents ?? 50000);
    // The fare accrues; the card is settled €500 at a time. The card that first
    // crosses the cap still goes through (create-first), so the sheet does NOT
    // block on a full buffer. Once a €500 step has failed (`billingBlocked`),
    // the next confirm is the retry mechanism. It is disabled only when there
    // is no card to retry.
    if (b.billingBlocked) {
      if (b.hasCardOnFile) {
        return {
          kind: "buffer",
          label: `${step} buffer payment due`,
          cta: "Retry & confirm",
          note: `We'll retry your ${step} buffer charge before sending this brief.`,
        };
      }
      return {
        kind: "buffer",
        label: "Buffer on hold",
        cta: "Confirm",
        note: `Add a card so we can settle your ${step} buffer and send this brief.`,
        blocked: true,
      };
    }
    // No per-brief charge to promise, so the CTA names no amount to pay now.
    return {
      kind: "buffer",
      label: b.hasCardOnFile ? `${step} buffer` : `${step} buffer · no card`,
      cta: "Confirm",
      note: b.hasCardOnFile
        ? `Added to your running total. We charge your card ${step} at a time. Price is locked.`
        : `Added to your running total - add a card so we can settle your ${step} steps.`,
    };
  }

  // per_card (default). Credit first - the server spends it first too.
  if (balance >= fareCents && fareCents > 0) {
    return {
      kind: "balance",
      label: `Balance · ${fmtEurCompact(balance)}`,
      cta: `Confirm & pay ${fmtEurCompact(fareCents)}`,
      note: "Comes straight out of your balance. Price is locked.",
    };
  }
  if (b.hasCardOnFile) {
    return {
      kind: "card",
      label: "Card on file",
      cta: `Confirm & pay ${fmtEurCompact(fareCents)}`,
      note: "Charged now. Price is locked at this amount.",
    };
  }
  return {
    kind: "no-card",
    label: "No card on file",
    cta: "Confirm & pay",
    note: "No card on file yet - add one to order.",
    blocked: true,
  };
}
